import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "../store/authStore";

export interface UserSearchResult {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
}

export const useSearchUsers = (searchTerm: string) => {
  const { profile } = useAuthStore();
  return useQuery<UserSearchResult[]>({
    queryKey: ["searchUsers", searchTerm, profile?.id],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2 || !profile?.id) {
        return [];
      }

      const userId = profile.id;

      // First get blocked user IDs
      const { data: blockedData } = await supabase
        .from("blocks")
        .select("blocked_id")
        .eq("blocker_id", userId);

      const blockedIds = blockedData?.map((b) => b.blocked_id) || [];

      // Search for users by username or full_name (case-insensitive)
      let query = supabase
        .from("profiles")
        .select("id, username, full_name, avatar_url, privacy_settings")
        .or(`username.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`);

      // Exclude blocked users
      if (blockedIds.length > 0) {
        query = query.not("id", "in", `(${blockedIds.join(",")})`);
      }

      // Exclude current user
      query = query.neq("id", userId);

      const { data, error } = await query.limit(10);

      if (error) {
        throw new Error(`Error searching users: ${error.message}`);
      }

      // Filter out users with private profiles
      const publicUsers = (data || []).filter((user) => {
        const privacySettings = user.privacy_settings;
        return privacySettings?.profile_public !== false;
      });

      return publicUsers.map((user) => ({
        id: user.id,
        username: user.username,
        full_name: user.full_name,
        avatar_url: user.avatar_url,
      }));
    },
    enabled: searchTerm.length >= 2 && !!profile?.id,
  });
};
